
import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, CreditCard, Sparkles, User, Settings, Building, Upload, Check, AlertCircle } from "lucide-react";
import { plans } from "@/config/plans";
import { resolveEffectivePlan, getOrgForUser } from "@/lib/orgBilling";
import { useOrg } from "@/hooks/useOrg";
import { trackStarterUpgrade, trackGrowthUpgrade } from "@/lib/tracking/conversion";
import { LifecycleOrgSettings } from "@/components/admin/LifecycleSettings";

export default function AdminSettings() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    // Profile State
    const [email, setEmail] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

    // Org State
    const [orgName, setOrgName] = useState("");
    const [sponsorName, setSponsorName] = useState("");
    const [sponsorAddress, setSponsorAddress] = useState("");
    const [supportEmail, setSupportEmail] = useState("");

    const [isUpdatingOrg, setIsUpdatingOrg] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);

    // --- Org Context (Deterministic) ---
    const { org, user, isLoading: isOrgLoading, error: orgError, refetch: refetchOrg } = useOrg();

    // Once user is loaded from useOrg, we know auth is checked.
    useEffect(() => {
        if (user) {
            setEmail(user.email || "");
            setIsCheckingAuth(false);
        } else if (!isOrgLoading && !user) {
            // Redirect if strictly no user found after loading
            navigate("/admin"); // Redirect to admin login, not auth generic
        }
    }, [user, isOrgLoading, navigate]);

    // Populate Org Name & Logo Preview
    useEffect(() => {
        const loadOrgAssets = async () => {
            if (org) {
                // Only update input if it's empty or we just loaded a different org (rare)
                setOrgName(org.name);
                setSponsorName(org.sponsor_name || "");
                setSponsorAddress(org.sponsor_address || "");
                setSupportEmail(org.support_email || "");

                // Prioritize logo_path
                if (org.logo_path) {
                    const { data } = await supabase.storage.from("org-assets").createSignedUrl(org.logo_path, 60 * 60);
                    if (data?.signedUrl) setLogoPreview(data.signedUrl);
                }
            }
        };
        loadOrgAssets();
    }, [org]);

    // --- Profile Handlers ---

    const handleUpdateEmail = async () => {
        if (!email) return;
        setIsUpdatingProfile(true);
        try {
            const { error } = await supabase.auth.updateUser({ email });
            if (error) throw error;
            toast({ title: "Email update initiated", description: "Check your new email for a confirmation link." });
        } catch (error: any) {
            toast({ title: "Update failed", description: error.message, variant: "destructive" });
        } finally {
            setIsUpdatingProfile(false);
        }
    };

    const handleUpdatePassword = async () => {
        if (!password || !currentPassword) return;
        if (password !== confirmPassword) {
            toast({ title: "Passwords do not match", variant: "destructive" });
            return;
        }
        if (password.length < 8) {
            toast({ title: "Password too short", description: "Must be at least 8 characters.", variant: "destructive" });
            return;
        }

        setIsUpdatingProfile(true);
        try {
            // Verify current password first
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            if (!currentUser?.email) throw new Error("User session invalid.");

            const { error: signInError } = await supabase.auth.signInWithPassword({
                email: currentUser.email,
                password: currentPassword
            });

            if (signInError) {
                toast({ title: "Current password incorrect", variant: "destructive" });
                setIsUpdatingProfile(false);
                return;
            }

            // Update password
            const { error } = await supabase.auth.updateUser({ password });
            if (error) throw error;

            toast({ title: "Password updated successfully" });
            setCurrentPassword("");
            setPassword("");
            setConfirmPassword("");
        } catch (error: any) {
            toast({ title: "Update failed", description: error.message, variant: "destructive" });
        } finally {
            setIsUpdatingProfile(false);
        }
    };

    // --- Org Handlers ---

    const handleUpdateOrgName = async () => {
        if (!org || !orgName.trim()) return;

        // No-op if name hasn't changed
        if (orgName.trim() === org.name) {
            toast({ title: "No changes detected" });
            return;
        }

        setIsUpdatingOrg(true);
        try {
            const { error } = await supabase
                .from("organizations")
                .update({ name: orgName.trim(), updated_at: new Date().toISOString() })
                .eq("id", org.id);
            if (error) throw error;

            toast({ title: "Organization name updated" });

            // Invalidate to refresh global state (header etc)
            await queryClient.invalidateQueries({ queryKey: ["org-context"] });
            await refetchOrg();

        } catch (error: any) {
            toast({ title: "Update failed", description: error.message, variant: "destructive" });
        } finally {
            setIsUpdatingOrg(false);
        }
    };

    const handleUpdateDefaults = async () => {
        if (!org) return;

        setIsUpdatingOrg(true);
        try {
            const { error } = await supabase
                .from("organizations")
                .update({
                    sponsor_name: sponsorName.trim() || null,
                    sponsor_address: sponsorAddress.trim() || null,
                    support_email: supportEmail.trim() || null,
                    updated_at: new Date().toISOString()
                } as any)
                .eq("id", org.id);
            if (error) throw error;

            toast({ title: "Default settings updated" });

            await queryClient.invalidateQueries({ queryKey: ["org-context"] });
            await refetchOrg();

        } catch (error: any) {
            toast({ title: "Update failed", description: error.message, variant: "destructive" });
        } finally {
            setIsUpdatingOrg(false);
        }
    };

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0 || !org) return;
        const file = e.target.files[0];

        setIsUploadingLogo(true);
        toast({ title: "Starting upload...", description: "Please wait while we process your image." });

        try {
            const fileExt = file.name.split('.').pop();
            // New path convention: {orgId}/logo/logo.{ext}
            // Use a timestamp or random string to avoid caching issues on same filename?
            // The requirement says "${org.id}/logo/logo.${ext}". We stick to that.
            // But we can add upsert: true.
            const filePath = `${org.id}/logo/logo.${fileExt}`;

            // Upload to 'org-assets' bucket
            const { error: uploadError } = await supabase.storage
                .from('org-assets')
                .upload(filePath, file, { upsert: true });

            if (uploadError) {
                if (uploadError.message.includes("Bucket not found")) {
                    throw new Error("Storage bucket 'org-assets' missing. Contact support.");
                }
                throw uploadError;
            }

            toast({ title: "Upload successful", description: "Updating organization profile..." });

            // Save logo_path to DB
            const { error: updateError } = await supabase
                .from("organizations")
                .update({ logo_path: filePath, updated_at: new Date().toISOString() })
                .eq("id", org.id);

            if (updateError) throw updateError;

            toast({ title: "Logo updated successfully" });

            // Refetch to ensure everything is in sync
            await queryClient.invalidateQueries({ queryKey: ["org-context"] });
            await refetchOrg();

            // Generate signed URL immediately for preview based on the known path
            const { data } = await supabase.storage.from("org-assets").createSignedUrl(filePath, 60 * 60);
            if (data?.signedUrl) setLogoPreview(data.signedUrl);

        } catch (error: any) {
            toast({ title: "Upload failed", description: error.message, variant: "destructive" });
            // Do NOT set logo preview if failed
        } finally {
            setIsUploadingLogo(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    // --- Billing Handlers ---

    const [isBillingAction, setIsBillingAction] = useState(false);

    const handleCheckout = async (planKey: 'starter' | 'growth') => {
        setIsBillingAction(true);
        try {
            const { data, error } = await supabase.functions.invoke('billing-checkout', {
                body: { planKey }
            });
            if (error) throw error;
            if (data?.upgraded) {
                // Fire upgrade event
                if (planKey === 'starter') {
                    trackStarterUpgrade(false);
                } else if (planKey === 'growth') {
                    trackGrowthUpgrade();
                }

                toast({
                    title: "Plan upgraded!",
                    description: "Your plan is now active.",
                });
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
                return;
            }
            if (data?.url) {
                localStorage.setItem('rk_pending_upgrade', planKey);
                window.location.href = data.url;
            }
        } catch (err: any) {
            console.error(err);
            toast({ title: "Failed to start checkout", description: err.message, variant: "destructive" });
        } finally {
            setIsBillingAction(false);
        }
    };

    const handlePortal = async () => {
        setIsBillingAction(true);
        try {
            const { data, error } = await supabase.functions.invoke('billing-portal', {});
            if (error) throw error;
            if (data?.url) {
                window.location.href = data.url;
            }
        } catch (err: any) {
            console.error(err);
            toast({ title: "Failed to open billing portal", description: err.message, variant: "destructive" });
        } finally {
            setIsBillingAction(false);
        }
    };

    if (isCheckingAuth || isOrgLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center flex-col gap-4">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-muted-foreground animate-pulse">Loading settings...</p>
            </div>
        );
    }

    if (orgError) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center flex-col gap-4">
                <AlertCircle className="w-8 h-8 text-destructive" />
                <p className="text-destructive font-medium">We couldn’t finish setting up your organization.</p>
                <Button onClick={() => refetchOrg()} variant="outline">Retry</Button>
            </div>
        );
    }

    if (!org) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center flex-col gap-4">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-muted-foreground">Setting up your organization...</p>
            </div>
        );
    }

    const defaultTab = searchParams.get("tab") || "profile";

    return (
        <div className="min-h-screen bg-background">
            <main className="container mx-auto px-4 py-8 max-w-4xl">
                <h1 className="text-3xl font-heading font-bold mb-8">Settings</h1>

                <Tabs defaultValue={defaultTab} className="w-full">
                    <TabsList className="mb-8 w-full justify-start h-auto p-1 bg-transparent border-b border-border rounded-none gap-6">
                        <TabsTrigger
                            value="profile"
                            className="text-base data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-2 pb-3"
                        >
                            Profile
                        </TabsTrigger>
                        <TabsTrigger
                            value="org"
                            className="text-base data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-2 pb-3"
                        >
                            Organization
                        </TabsTrigger>
                        <TabsTrigger
                            value="billing"
                            className="text-base data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-2 pb-3"
                        >
                            Billing
                        </TabsTrigger>
                    </TabsList>

                    {/* Profile Tab */}
                    <TabsContent value="profile" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Personal Information</CardTitle>
                                <CardDescription>Manage your account details.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Email Address</Label>
                                    <Input value={email} disabled className="bg-muted text-muted-foreground" />
                                    <p className="text-xs text-muted-foreground">Email changes are currently disabled.</p>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Change Password</CardTitle>
                                <CardDescription>Ensure your account is using a strong password.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Current Password</Label>
                                    <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="••••••••" />
                                </div>
                                <div className="space-y-2">
                                    <Label>New Password</Label>
                                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Confirm New Password</Label>
                                    <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" />
                                </div>
                            </CardContent>
                            <CardFooter>
                                <Button onClick={handleUpdatePassword} disabled={isUpdatingProfile || !password || !currentPassword}>
                                    {isUpdatingProfile && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Save Password
                                </Button>
                            </CardFooter>
                        </Card>
                    </TabsContent>

                    {/* Org Tab */}
                    <TabsContent value="org" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Organization Details</CardTitle>
                                <CardDescription>Manage your company profile.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-2">
                                    <Label>Organization Name</Label>
                                    <div className="flex gap-3">
                                        <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Organization Name" />
                                        <Button onClick={handleUpdateOrgName} disabled={isUpdatingOrg || !orgName.trim() || orgName.trim() === org?.name}>Save</Button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Organization Logo</Label>
                                    <div className="flex items-center gap-6">
                                        {logoPreview ? (
                                            <div className="h-20 w-20 rounded-lg border overflow-hidden bg-gray-50 flex items-center justify-center relative group">
                                                <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                                            </div>
                                        ) : (
                                            <div className="h-20 w-20 rounded-lg border border-dashed bg-gray-50 flex items-center justify-center text-muted-foreground">
                                                <User className="h-8 w-8 opacity-20" />
                                            </div>
                                        )}
                                        <div className="flex flex-col gap-2">
                                            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isUploadingLogo}>
                                                {isUploadingLogo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                                                Upload New Logo
                                            </Button>
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                className="hidden"
                                                accept="image/png, image/jpeg"
                                                onChange={handleLogoUpload}
                                            />
                                            <p className="text-xs text-muted-foreground">Recommended: 400x400px PNG or JPG.</p>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Contest Defaults</CardTitle>
                                <CardDescription>Pre-fill these values when creating new contests.</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Default Sponsor Name</Label>
                                    <Input value={sponsorName} onChange={(e) => setSponsorName(e.target.value)} placeholder="e.g., Acme Corp" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Default Sponsor Address</Label>
                                    <Input value={sponsorAddress} onChange={(e) => setSponsorAddress(e.target.value)} placeholder="e.g., 123 Main St, New York, NY" />
                                    <p className="text-xs text-muted-foreground">Required on every lifecycle email. Sending stays blocked until this is saved.</p>
                                </div>
                                <div className="space-y-2">
                                    <Label>Default Support Email</Label>
                                    <Input value={supportEmail} type="email" onChange={(e) => setSupportEmail(e.target.value)} placeholder="e.g., support@acme.com" />
                                </div>
                            </CardContent>
                            <CardFooter>
                                <Button onClick={handleUpdateDefaults} disabled={isUpdatingOrg}>
                                    {isUpdatingOrg && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Save Defaults
                                </Button>
                            </CardFooter>
                        </Card>
                        {org?.id && <LifecycleOrgSettings organizationId={org.id} />}
                    </TabsContent>

                    {/* Billing Tab */}
                    <TabsContent value="billing" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle>Current Plan</CardTitle>
                                        <CardDescription>Manage your subscription and billing details.</CardDescription>
                                    </div>
                                    {/* Button moved inside Render logic or just hidden if free */}
                                </div>
                            </CardHeader>
                            <CardContent>
                                {(() => {
                                    const effectivePlan = org ? resolveEffectivePlan(org) : 'free';
                                    const isPaid = effectivePlan !== 'free';

                                    return (
                                        <>
                                            {/* Current Plan Summary */}
                                            <div className="rounded-xl bg-card border shadow-sm p-6 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                                <div className="flex items-center gap-4">
                                                    <div className={`h-12 w-12 rounded-full flex items-center justify-center ${isPaid ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                                                        {isPaid ? <Sparkles className="h-6 w-6" /> : <User className="h-6 w-6" />}
                                                    </div>
                                                    <div>
                                                        <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                                                            {effectivePlan === 'growth' ? 'Growth Plan' : effectivePlan === 'starter' ? 'Starter Plan' : 'Free Plan'}
                                                            {org?.plan_status === 'trialing' && <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full uppercase font-bold tracking-wide">Trial</span>}
                                                        </h3>
                                                        <p className="text-muted-foreground">
                                                            {org?.plan_status === 'trialing' && org.trial_end
                                                                ? `Trial ends ${new Date(org.trial_end).toLocaleDateString()}`
                                                                : isPaid ? 'Billing active' : 'limited features'}
                                                        </p>
                                                    </div>
                                                </div>
                                                {isPaid && (
                                                    <Button variant="outline" onClick={handlePortal} disabled={isBillingAction} className="w-full md:w-auto">
                                                        <CreditCard className="w-4 h-4 mr-2" />
                                                        Manage Subscription
                                                    </Button>
                                                )}
                                            </div>

                                            {/* Upgrade Callout */}
                                            {effectivePlan !== 'growth' && (
                                                <div className="mb-8 bg-gradient-to-r from-primary/5 via-primary/10 to-transparent border-l-4 border-primary p-4 rounded-r-lg">
                                                    <h4 className="font-semibold text-foreground flex items-center gap-2">
                                                        <Sparkles className="w-4 h-4 text-primary" />
                                                        Unlock Full Power
                                                    </h4>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        Upgrade to the <strong>Growth</strong> plan for unlimited contests, advanced analytics, and priority support.
                                                    </p>
                                                </div>
                                            )}

                                            {/* Plans Grid */}
                                            <div className="grid gap-6 md:grid-cols-3">
                                                {(() => {
                                                    const planRank = (plan: string) => {
                                                        if (plan === 'growth') return 2;
                                                        if (plan === 'starter') return 1;
                                                        return 0;
                                                    };
                                                    const currentRank = planRank(effectivePlan);

                                                    return plans.map((p) => {
                                                        const isCurrent = effectivePlan === p.id;
                                                        const isGrowth = p.id === 'growth';
                                                        // Only show 'Most Popular' if not currently creating visual noise, but safe to add
                                                        const isPopular = isGrowth;
                                                        const showTrialOffer = p.id === 'starter' && org?.plan_key === 'free' && !org?.stripe_customer_id;
                                                        const cardRank = planRank(p.id);

                                                        return (
                                                            <div
                                                                key={p.id}
                                                                className={`relative rounded-xl flex flex-col transition-all duration-300 ${isGrowth
                                                                    ? 'border-2 border-primary/50 shadow-2xl shadow-primary/10 scale-[1.02] bg-gradient-to-b from-primary/5 to-card z-10 ring-1 ring-primary/20'
                                                                    : 'border bg-card/50 hover:bg-card hover:shadow-md'
                                                                    } p-6`}
                                                            >
                                                                {isPopular && (
                                                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary to-violet-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg shadow-primary/20 uppercase tracking-wide flex items-center gap-1.5 whitespace-nowrap">
                                                                        <Sparkles className="w-3 h-3 fill-white/20" />
                                                                        Most Popular
                                                                    </div>
                                                                )}

                                                                <div className="mb-6">
                                                                    <h4 className="font-heading font-bold text-lg text-foreground">{p.name}</h4>
                                                                    <div className="mt-2 flex items-baseline gap-1">
                                                                        <span className="text-3xl font-bold">{p.price}</span>
                                                                        {p.price !== 'Free' && p.id !== 'free' && <span className="text-muted-foreground font-medium">/mo</span>}
                                                                    </div>
                                                                    {p.id === 'free' && (
                                                                        <p className="text-sm text-muted-foreground mt-2">{p.description}</p>
                                                                    )}
                                                                    {showTrialOffer && (
                                                                        <span style={{
                                                                            display: "inline-block",
                                                                            backgroundColor: "#f97316",
                                                                            color: "#ffffff",
                                                                            fontSize: 11,
                                                                            fontWeight: 600,
                                                                            padding: "3px 10px",
                                                                            borderRadius: 999,
                                                                            letterSpacing: "0.02em",
                                                                            marginTop: 8,
                                                                            marginBottom: 8,
                                                                        }}>
                                                                            🎉 First 3 months free
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div className="flex-1 mb-8">
                                                                    <ul className="space-y-3">
                                                                        {p.features.map((feature, i) => (
                                                                            <li key={i} className="flex items-start gap-3 text-sm">
                                                                                <div className={`mt-0.5 rounded-full p-0.5 ${isGrowth ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                                                                                    <Check className="w-3 h-3" />
                                                                                </div>
                                                                                <span className="text-foreground/90">{feature}</span>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>

                                                                <div className="mt-auto">
                                                                    {cardRank > currentRank ? (
                                                                        <Button
                                                                            onClick={() => handleCheckout(p.id as 'starter' | 'growth')}
                                                                            disabled={isBillingAction}
                                                                            className={`w-full font-bold shadow-md transition-all duration-300 ${isGrowth
                                                                                ? '!bg-gradient-to-r !from-primary !to-violet-600 hover:!to-violet-700 !text-white hover:!opacity-90 hover:shadow-lg hover:shadow-primary/25 hover:scale-[1.02] border-0'
                                                                                : ''
                                                                                }`}
                                                                            variant={isGrowth ? "default" : "outline"}
                                                                        >
                                                                            {isBillingAction && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                                                            Upgrade
                                                                        </Button>
                                                                    ) : cardRank === currentRank ? (
                                                                        <Button disabled variant="secondary" className="w-full font-semibold opacity-80">Current Plan</Button>
                                                                    ) : (
                                                                        <a
                                                                            href="mailto:hello@rafflekit.app?subject=Downgrade Request"
                                                                            className="text-sm text-muted-foreground hover:text-foreground transition-colors text-center block py-2"
                                                                        >
                                                                            Contact us to Downgrade
                                                                        </a>
                                                                    )}
                                                                    {showTrialOffer && (
                                                                        <p style={{
                                                                            fontSize: 11,
                                                                            color: "#94a3b8",
                                                                            textAlign: "center",
                                                                            marginTop: 6,
                                                                            lineHeight: 1.4,
                                                                        }}>
                                                                            No charge until after your first 3 months.
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    });
                                                })()}
                                            </div>

                                            {/* Trust Footer */}
                                            < div className="mt-12 text-center border-t pt-8" >
                                                <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                                                    <p className="flex items-center justify-center gap-1">
                                                        <Sparkles className="w-3 h-3" />
                                                        Secure payment via Stripe. Cancel anytime.
                                                    </p>
                                                    <p>
                                                        Need help picking a plan? <a href="mailto:hello@rafflekit.app" className="font-medium text-primary hover:underline">Contact Support</a>
                                                    </p>
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main >


        </div >
    );
}
